import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/Dialog';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { Button } from '@/routes/_embaUserLayout/-components/Button';
import { LessonTitle } from '@/routes/_embaUserLayout/-components/LessonTitle';
import { useInputTextValidator } from '@/routes/_embaUserLayout/-hooks/useInputTextValidator';
import { useLeaveBlocker } from '@/routes/_embaUserLayout/-hooks/useLeaveBlocker';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import type React from 'react';
import { useEffect, useState } from 'react';
import styles from './index.module.scss';

export const COURSE_LESSON_GOAL_SETTING_FRAGMENT = graphql(/* GraphQL */ `
  fragment EmbaCourseLessonGoalSetting on CourseLessonGoalSetting {
    id
    userAnswer {
      id
      content
    }
  }
`);

export type CourseLessonGoalSettingPageProps = {
  content: FragmentType<typeof COURSE_LESSON_GOAL_SETTING_FRAGMENT>;
  lessonName: string;
  chapterName: string;
  onEmbaCompleteCourseLesson: () => Promise<void>;
  answerGoalSetting: (content: string) => Promise<void>;
  answerGoalSettingLoading: boolean;
  isAnsweredModalOpen: boolean;
  closeAnsweredModal: () => void;
};

export const CourseLessonGoalSettingPageComponent = ({
  content,
  lessonName,
  chapterName,
  onEmbaCompleteCourseLesson,
  answerGoalSetting,
  answerGoalSettingLoading,
  isAnsweredModalOpen,
  closeAnsweredModal,
}: CourseLessonGoalSettingPageProps) => {
  const embaCourseLessonGoalSettingData = useFragment(
    COURSE_LESSON_GOAL_SETTING_FRAGMENT,
    content,
  );

  const [answer, setAnswer] = useState('');
  const userAnswer = embaCourseLessonGoalSettingData.userAnswer?.content;
  const answered = !!userAnswer;

  const { valid, isValid } = useInputTextValidator(answer);
  useEffect(() => {
    valid();
  }, [valid]);

  const { onBlocking, unBlocking, initBlocking } = useLeaveBlocker();
  useEffect(
    () => (answered ? unBlocking() : onBlocking()),
    [answered, unBlocking, onBlocking],
  );
  initBlocking();

  return (
    <div>
      <LessonTitle lessonName={lessonName} chapterName={chapterName} />
      <div className={styles.goalSettingWrapper}>
        <div className={styles.questionArea}>
          <div className={styles.questionContentArea}>
            <div className={styles.questionHeader}>
              <div className={styles.questionLabel}>問</div>
            </div>
            <p className={styles.questionContent}>
              では、学習の目標を設定しましょう。
            </p>
            <textarea
              value={userAnswer ?? answer}
              onChange={(e) => setAnswer(e.target.value)}
              disabled={answered}
              className={styles.goalSettingQuestionForm}
              maxLength={1000}
            />
            <div className={styles.goalSettingQuestionFormCount}>
              <span>
                本設問の回答は受講管理者に共有されます。送信後の修正はできません。
              </span>
              {!answered && <span>(1000文字以内)</span>}
            </div>

            <div className={styles.answerButton}>
              {!answered ? (
                <Button
                  onClick={() => {
                    answerGoalSetting(answer);
                  }}
                  disabled={!isValid}
                  // Loadingが終わってからanswer取得するまでに時間がかかるため、isAnsweredModalOpenも利用
                  loading={answerGoalSettingLoading || isAnsweredModalOpen}
                >
                  決定
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    unBlocking();
                    onEmbaCompleteCourseLesson();
                  }}
                >
                  終了
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={isAnsweredModalOpen && answered}
        onOpenChange={closeAnsweredModal}
      >
        <VisuallyHidden>
          <DialogDescription>演習問題解答結果のモーダル</DialogDescription>
        </VisuallyHidden>

        <DialogContent>
          <div className={styles.AnsweredDialog}>
            <DialogTitle className={styles.AnsweredDialogTitle}>
              目標設定が完了しました！
              <br />
              目標達成のためにがんばりましょう！
            </DialogTitle>
            <Button
              onClick={() => {
                unBlocking();
                onEmbaCompleteCourseLesson();
              }}
            >
              終了
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
